import React, { useState } from 'react';
import { API_BASE_URL } from '../services/index';
import { motion } from 'framer-motion';
import { AlertTriangle, Calendar, ExternalLink, Lock } from 'lucide-react';
import { StandardCard } from '../styles/standardStyles';
import { isLocalDevHost } from '../utils/localDevHost';
import MySubscriptionModal from './MySubscriptionModal';
import './SubscriptionLockModal.css';

/**
 * SubscriptionLockModal - Full-screen lock modal shown when subscription is inactive.
 * Blocks all application access until the user renews their subscription.
 */
const SubscriptionLockModal = ({ email }) => {
  const [checking, setChecking] = useState(false);
  const [checkMsg, setCheckMsg] = useState('');
  const [showMySubscription, setShowMySubscription] = useState(false);

  // Re-read the subscription status from the server. Lets a customer whose
  // window INVEXIS just moved get back in without signing out and in again.
  const handleCheckAgain = async () => {
    setChecking(true);
    setCheckMsg('');
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_BASE_URL}/auth/user`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await r.json();
      if (r.ok && data && data.success) {
        const stored = JSON.parse(localStorage.getItem('user') || '{}');
        localStorage.setItem('user', JSON.stringify({
          ...stored,
          subscription: data.subscription,
          isTrialActive: data.isTrialActive,
          trialExpiryDate: data.trialExpiryDate,
          subscriptionSource: data.subscriptionSource,
          subscriptionWindow: data.subscriptionWindow
        }));
        if (data.subscription === true || data.isTrialActive === true) {
          window.location.reload();
          return;
        }
        setCheckMsg('Still inactive. If you have renewed, please wait a moment and try again.');
      } else {
        setCheckMsg('Could not verify right now. Please try again.');
      }
    } catch (e) {
      setCheckMsg('Could not verify right now. Please try again.');
    } finally {
      setChecking(false);
    }
  };

  const handleRenewPayment = () => {
    const queryParams = new URLSearchParams();
    queryParams.append('plan', 'atomic');
    if (email) {
      queryParams.append('email', email);
    }
    window.open(`https://welcome.invexis.co.in/?${queryParams.toString()}#pricing`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="sub-lock-overlay">
      <motion.div
        className="sub-lock-shell"
        initial={{ opacity: 0, y: 40, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <motion.div className="sub-lock-float-wrap">
          <motion.div
            className="sub-lock-float"
            animate={{ y: [0, -6, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          >
            <div className="sub-lock-float-inner">
              <Lock className="sub-lock-float-icon" />
            </div>
            <motion.div
              className="sub-lock-float-pulse"
              animate={{ scale: [1, 1.45], opacity: [0.65, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeOut' }}
            />
          </motion.div>
        </motion.div>

        <StandardCard className="sub-lock-modal" hover={false}>
          <motion.div
            className="sub-lock-topline"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: 0.5, duration: 0.8, ease: 'easeOut' }}
            style={{ transformOrigin: 'left' }}
          />

          <div className="sub-lock-card-inner">
            <motion.div
              className="sub-lock-icon"
              initial={{ rotate: -15 }}
              animate={{ rotate: [0, -5, 5, 0] }}
              transition={{ delay: 0.8, duration: 0.5 }}
            >
              <AlertTriangle className="sub-lock-warning-icon" />
            </motion.div>

            <motion.h2
              className="sub-lock-heading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              Subscription Inactive
            </motion.h2>

            <motion.p
              className="sub-lock-description"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
            >
              Your subscription has expired or is currently inactive.
              Please renew your subscription to continue using the application.
            </motion.p>

            <div className="sub-lock-actions">
              {/* Local-development only: deployed customers renew through Renew Payment. */}
              {isLocalDevHost() && (
                <motion.button
                  type="button"
                  className="sub-lock-renew-button"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.9 }}
                  whileHover={{ scale: 1.02, x: 2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowMySubscription(true)}
                  style={{ backgroundColor: '#2563eb', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                >
                  <Calendar className="sub-lock-renew-icon" />
                  <span>My Subscription Period</span>
                </motion.button>
              )}
              <button
                type="button"
                className="sub-lock-renew-button"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.95 }}
                onClick={handleRenewPayment}
              >
                <ExternalLink className="sub-lock-renew-icon" />
                <span>Renew Payment</span>
              </button>
              <button
                type="button"
                className="sub-lock-renew-button"
                style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.35)', opacity: checking ? 0.6 : 1 }}
                onClick={handleCheckAgain}
                disabled={checking}
              >
                <span>{checking ? 'Checking…' : "I've renewed — check again"}</span>
              </button>
            </div>
            {checkMsg && <p style={{ marginTop: 8, fontSize: 12, opacity: 0.85 }}>{checkMsg}</p>}
          </div>
        </StandardCard>

        <motion.div
          className="sub-lock-badge"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
        >
          <Lock className="sub-lock-badge-icon" />
          <span>Access locked until subscription is renewed</span>
        </motion.div>
      </motion.div>

      {showMySubscription && (
        <MySubscriptionModal onClose={() => setShowMySubscription(false)} />
      )}
    </div>
  );
};

export default SubscriptionLockModal;
